import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { comics } from '../data/comics';
import { useEntitlements } from '../hooks/useEntitlements';
import { useLanguage } from '../contexts/LanguageContext';
import { AuthShell } from '../components/AuthShell';

const SALT = '_FOOTPARADISE_SALT_2024';

async function hashCode(code) {
  const normalizedCode = code.toUpperCase().trim();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalizedCode + SALT);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

export default function Redeem() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { refresh } = useEntitlements();

  const comicId = searchParams.get('comic');
  const comic = comics.find((c) => c.id === Number(comicId));
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!comic) {
    return (
      <AuthShell title={language === 'es' ? 'Cómic no encontrado' : 'Comic not found'}>
        <button className="fp-btn fp-btn--lg fp-btn--block" onClick={() => navigate('/')}>
          {language === 'es' ? 'Volver al inicio' : 'Back to home'}
        </button>
      </AuthShell>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    setLoading(true);
    try {
      const codeHash = await hashCode(code);
      const { data, error: rpcError } = await supabase.rpc('redeem_code', {
        p_comic_id: Number(comicId),
        p_code_hash: codeHash,
      });

      if (rpcError) throw rpcError;

      if (data === true) {
        await refresh();
        setSuccess(true);
      } else {
        setError(language === 'es' ? 'Código inválido para este comic' : 'Invalid code for this comic');
      }
    } catch (err) {
      setError(language === 'es' ? 'Error al canjear el código' : 'Error redeeming code');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <AuthShell title={language === 'es' ? '¡Desbloqueado!' : 'Unlocked!'} subtitle={comic.title}>
        <button className="fp-btn fp-btn--gold fp-btn--lg fp-btn--block" onClick={() => navigate(`/comic/${comicId}`)}>
          {language === 'es' ? 'Leer ahora' : 'Read now'}
        </button>
        <button type="button" className="fp-link" style={styles.center} onClick={() => navigate('/')}>
          {language === 'es' ? 'Volver al inicio' : 'Back to home'}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={language === 'es' ? 'Canjear código' : 'Redeem code'}
      subtitle={comic.title}
      onSubmit={handleSubmit}
    >
      <input
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder={language === 'es' ? 'Código de compra' : 'Purchase code'}
        className="fp-input"
        required
        autoFocus
      />

      {error && <div className="fp-msg fp-msg--error">{error}</div>}

      <button type="submit" className="fp-btn fp-btn--gold fp-btn--lg fp-btn--block" disabled={loading}>
        {loading ? '...' : language === 'es' ? 'Canjear' : 'Redeem'}
      </button>

      <button type="button" className="fp-link" style={styles.center} onClick={() => navigate(-1)}>
        {language === 'es' ? '← Volver' : '← Back'}
      </button>
    </AuthShell>
  );
}

const styles = {
  center: {
    alignSelf: 'center',
  },
};
